import { useQuery } from '@tanstack/react-query';

import { QueryKey } from '@/config/query-client';
import type { OrganizationInvestor } from '@/api/v4/organization.api';
import { fetchOrganizationInvestors } from '@/api/v4/organization.api';
import { fetchSpotlightOrganizationInvestors } from '@/api/v4/spotlight.api';
import type { InvestorOption } from '@/types';
import {
  type CompanyFetchMode,
  useCompanyFetchMode,
} from '../company-fetch-context';

const companyInvestorsFetchers: Record<
  CompanyFetchMode,
  (companyId: string) => ReturnType<typeof fetchOrganizationInvestors>
> = {
  general: fetchOrganizationInvestors,
  spotlight: fetchSpotlightOrganizationInvestors,
};

export const useCompanyInvestors = (companyId: string | null) => {
  const fetchMode = useCompanyFetchMode();

  const getInvestors = async () => {
    if (!companyId)
      return {
        investors: [],
        poweredBy: null,
        lastEditedAt: null,
      };

    return companyInvestorsFetchers[fetchMode](companyId);
  };

  return useQuery({
    queryKey: [QueryKey.CompanyInvestors, fetchMode, companyId],
    queryFn: getInvestors,
    enabled: !!companyId,
    staleTime: 1000 * 60 * 5,
  });
};

export const mapOrganizationInvestorsToOptions = (
  investors: OrganizationInvestor[],
): InvestorOption[] => {
  return investors.map(i => ({
    value: i.id,
    label: i.name,
    investorId: i.id,
    crunchbaseId: i.crunchbaseId,
    website: i.website,
    logoUrl: i.logoUrl,
    description: i.description,
    source: i.source,
  }));
};
